/**
 * PDFViewer Component
 * Reusable PDF viewer dialog component
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import { withoutBackdropClose } from "../../utils/withoutBackdropClose";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { Document, Page, pdfjs } from "react-pdf";
import { PDF_VIEWER_ABOVE_ELEVATED_DIALOG_Z_INDEX_OFFSET } from "../../constants/dialogZIndex";
import { DIALOG_CLOSE_BUTTON_SX } from "../../constants/dialogCloseButtonStyles";
import {
  fetchPdfBlob,
  openPdfUrlInNewTabForPreview,
} from "../../utils/openPdfInNewTab";

interface PDFViewerProps {
  open: boolean;
  pdfUrl: string;
  title?: string;
  onClose: () => void;
  /**
   * When true, z-index is above dialogs that use {@link ELEVATED_DIALOG_Z_INDEX_OFFSET}
   * (e.g. AI Impact Assessment Results modal).
   */
  stackAboveElevatedModal?: boolean;
}

function renderPdfContent(params: {
  pdfUrl: string;
  pdfFile: { url: string };
  pdfOptions: {
    cMapUrl: string;
    standardFontDataUrl: string;
  };
  shouldRender: boolean;
  isLoading: boolean;
  numPages: number | null;
  scale: number;
  error: string | null;
  onLoadSuccess: (args: { numPages: number }) => void;
  onLoadError: (error: Error) => void;
  onOpenInNewTab: () => void;
}) {
  const {
    pdfUrl,
    pdfFile,
    pdfOptions,
    shouldRender,
    isLoading,
    numPages,
    scale,
    error,
    onLoadSuccess,
    onLoadError,
    onOpenInNewTab,
  } = params;

  if (!pdfUrl) return <Typography>No document selected</Typography>;

  if (!shouldRender) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "500px",
        }}
      >
        <Typography>Preparing PDF...</Typography>
      </Box>
    );
  }

  const pages =
    !isLoading && numPages
      ? Array.from({ length: numPages }, (_, index) => (
          <Box
            key={`page_${index + 1}`}
            sx={{
              mb: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <Page
              pageNumber={index + 1}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              width={700 * scale}
              scale={scale}
            />
          </Box>
        ))
      : null;

  return (
    <Document
      file={pdfFile}
      onLoadSuccess={onLoadSuccess}
      onLoadError={onLoadError}
      options={pdfOptions}
      loading={
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "500px",
          }}
        >
          <Typography>Loading PDF...</Typography>
        </Box>
      }
      error={
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "500px",
            gap: 2,
          }}
        >
          <Typography color="error">
            Failed to load PDF
            {error && `: ${error}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please try opening in a new tab
          </Typography>
          <Button variant="contained" onClick={onOpenInNewTab}>
            Open In New Tab
          </Button>
        </Box>
      }
    >
      {pages}
    </Document>
  );
}

function getFilenameFromDisposition(disposition: string): string | null {
  if (!disposition) return null;
  const filenameMatch = /filename="([^"]+)"/.exec(disposition);
  if (filenameMatch?.[1]) return filenameMatch[1];
  const filenameStar = /filename\*=UTF-8''(.+)/.exec(disposition);
  if (filenameStar?.[1]) return decodeURIComponent(filenameStar[1]);
  return null;
}

function normalizePdfFilenameFromPath(pathname: string): string | null {
  const pathParts = pathname.split("/");
  const rawName = pathParts[pathParts.length - 1];
  if (!rawName) return null;

  let filename = decodeURIComponent(rawName);

  const uuidRe =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  // Pattern 1: "<name>_bt-<uuid>[.pdf]"
  const btMarker = "_bt-";
  const btIdx = filename.lastIndexOf(btMarker);
  if (btIdx > 0) {
    const prefix = filename.slice(0, btIdx);
    const tail = filename.slice(btIdx + btMarker.length);
    const uuidPart = tail.toLowerCase().endsWith(".pdf")
      ? tail.slice(0, -".pdf".length)
      : tail;
    if (uuidRe.test(uuidPart)) {
      filename = prefix;
    }
  } else {
    // Pattern 2: "<uuid>_<name>"
    const underscoreIdx = filename.indexOf("_");
    if (underscoreIdx > 0) {
      const maybeUuid = filename.slice(0, underscoreIdx);
      const rest = filename.slice(underscoreIdx + 1);
      if (uuidRe.test(maybeUuid) && rest) {
        filename = rest;
      }
    }
  }

  if (!filename.toLowerCase().endsWith(".pdf")) filename += ".pdf";
  return filename;
}

function extractPdfFilename(url: string, title?: string): string {
  const defaultName =
    title && title !== "Document Preview" ? title : "document.pdf";
  if (!url) return defaultName;

  try {
    const urlObj = new URL(url);

    const disposition = urlObj.searchParams.get("response-content-disposition");
    const fromDisposition = getFilenameFromDisposition(disposition || "");
    if (fromDisposition) return fromDisposition;

    if (!title || title === "Document Preview") {
      const fromPath = normalizePdfFilenameFromPath(urlObj.pathname);
      if (fromPath) return fromPath;
    }
  } catch {
    // ignore
  }
  return defaultName;
}

async function downloadPdf(pdfUrl: string, filename: string) {
  const blob = await fetchPdfBlob(pdfUrl);
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => {
    window.URL.revokeObjectURL(blobUrl);
  }, 1000);
}

export const PDFViewer: React.FC<PDFViewerProps> = ({
  open,
  pdfUrl,
  title = "Document Preview",
  onClose,
  stackAboveElevatedModal = false,
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [scale, setScale] = useState(1.0);

  const MIN_SCALE = 0.5;
  const MAX_SCALE = 2.0;
  const SCALE_STEP = 0.25;

  const pdfFile = useMemo(() => ({ url: pdfUrl }), [pdfUrl]);
  const pdfOptions = useMemo(
    () => ({
      cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
      standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
    }),
    [],
  );

  const handleLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setError(null);
    setIsLoading(false);
  }, []);

  const handleLoadError = useCallback((error: Error) => {
    console.error("PDF load error:", error);
    setError(error.message || "Failed to load PDF");
    setIsLoading(false);
  }, []);

  const handleOpenInNewTab = useCallback(() => {
    void openPdfUrlInNewTabForPreview(pdfUrl, title || "PDF Preview");
  }, [pdfUrl, title]);

  const handleDownload = () => {
    if (isDownloading) return;
    setIsDownloading(true);

    const filename = extractPdfFilename(pdfUrl, title);
    downloadPdf(pdfUrl, filename)
      .then(() => setIsDownloading(false))
      .catch((error) => {
        console.error("Download failed:", error);
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = filename;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          setIsDownloading(false);
        }, 100);
      });
  };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + SCALE_STEP, MAX_SCALE));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - SCALE_STEP, MIN_SCALE));
  };

  // Reset loading state when dialog opens
  useEffect(() => {
    if (open) {
      setIsLoading(true);
      setNumPages(null);
      setError(null);
      setScale(1.0); // Reset zoom level
      // Delay rendering to ensure clean state
      setShouldRender(false);
      const timer = setTimeout(() => {
        setShouldRender(true);
      }, 100);

      return () => {
        clearTimeout(timer);
        setShouldRender(false);
      };
    } else {
      setShouldRender(false);
    }
  }, [open, pdfUrl]);

  return (
    <Dialog
      open={open}
      onClose={withoutBackdropClose(onClose)}
      maxWidth="md"
      fullWidth
      slotProps={
        stackAboveElevatedModal
          ? {
              root: {
                sx: {
                  zIndex: (theme) =>
                    theme.zIndex.modal +
                    PDF_VIEWER_ABOVE_ELEVATED_DIALOG_Z_INDEX_OFFSET,
                },
              },
            }
          : undefined
      }
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">{title}</Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {/* Zoom Controls */}
            <Tooltip title="Zoom Out">
              <span>
                <IconButton
                  size="small"
                  onClick={handleZoomOut}
                  disabled={scale <= MIN_SCALE}
                  sx={{ color: "text.secondary" }}
                >
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Typography variant="body2" sx={{ minWidth: 50, textAlign: "center" }}>
              {Math.round(scale * 100)}%
            </Typography>
            <Tooltip title="Zoom In">
              <span>
                <IconButton
                  size="small"
                  onClick={handleZoomIn}
                  disabled={scale >= MAX_SCALE}
                  sx={{ color: "text.secondary" }}
                >
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {/* Download Button */}
            <Tooltip title="Download PDF">
              <IconButton
                size="small"
                onClick={handleDownload}
                disabled={isDownloading}
                sx={{ color: "text.secondary" }}
              >
                {isDownloading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <DownloadIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Open In New Tab">
              <span>
                <IconButton
                  size="small"
                  onClick={handleOpenInNewTab}
                  disabled={!pdfUrl}
                  aria-label="Open In New Tab"
                  sx={{ color: "text.secondary" }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {/* Close Button */}
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={DIALOG_CLOSE_BUTTON_SX}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ overflowX: "auto", overflowY: "auto" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: "600px",
            width: "fit-content",
            margin: "0 auto",
          }}
        >
          {renderPdfContent({
            pdfUrl,
            pdfFile,
            pdfOptions,
            shouldRender,
            isLoading,
            numPages,
            scale,
            error,
            onLoadSuccess: handleLoadSuccess,
            onLoadError: handleLoadError,
            onOpenInNewTab: handleOpenInNewTab,
          })}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "flex-end", px: 2, py: 1.5 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleOpenInNewTab}
          disabled={!pdfUrl}
          startIcon={<OpenInNewIcon fontSize="small" />}
          sx={{ textTransform: "none" }}
        >
          Open In New Tab
        </Button>
      </DialogActions>
    </Dialog>
  );
};
